"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAgentApiSubscribeConversation = useAgentApiSubscribeConversation;
// src/apis/agent-api/hooks/useSubscribeConversation.ts
const recoil_1 = require("recoil");
const aws_amplify_1 = require("aws-amplify");
const react_1 = require("react");
const AgentApiStore = __importStar(require("../state"));
const state_1 = require("../state");
const shared_1 = require("@heartcheck/shared");
// ---- Debug toggle -----------------------------------------------------------
const DEBUG_SUBS = (typeof process !== "undefined" &&
    process?.env?.REACT_APP_DEBUG_SUBS === "true");
// ---- Auth mode (avoid enum import for cross-version Amplify) ----------------
const AUTH_MODE = "AMAZON_COGNITO_USER_POOLS";
// ---- Allowed agents filter (optional) ---------------------------------------
/**
 * Allowed agents filter (prevents noise).
 * - By default, allow ALL agents (no filter).
 * - To whitelist, set env: REACT_APP_ALLOWED_AGENT_IDS="agent-start,agent-question-and-answer"
 * - To explicitly allow all: REACT_APP_ALLOWED_AGENT_IDS="*"
 */
const RAW_ALLOWED = (typeof process !== "undefined" &&
    process?.env?.REACT_APP_ALLOWED_AGENT_IDS) ||
    "";
const ALLOWED_AGENT_IDS = !RAW_ALLOWED || RAW_ALLOWED.trim() === "*"
    ? null
    : RAW_ALLOWED.split(",").map((s) => s.trim()).filter(Boolean);
function isAgentAllowed(agentId) {
    if (!agentId)
        return true; // cannot determine -> allow
    if (ALLOWED_AGENT_IDS === null)
        return true; // no filter in effect
    return ALLOWED_AGENT_IDS.includes(agentId);
}
const nowIso = () => {
    try {
        return new Date().toISOString();
    }
    catch {
        return undefined;
    }
};
const REGISTRY = new Map();
/* --------------------------
   GraphQL subscriptions
---------------------------*/
function subscribeConversationMessages(conversationId) {
    const query = `
    subscription OnConversationEvents($cid: ID!) {
      onConversationEvents(conversationId: $cid) {
        id
        sender
        timestamp
        conversationId
        userId
        agentId
        event {
          message
          innerDialog
          actionRequested
          actionResult
        }
      }
    }
  `;
    return aws_amplify_1.API.graphql({
        query,
        variables: { cid: conversationId },
        authMode: AUTH_MODE,
    }); // Observable
}
function subscribeConversationMetadata(conversationId) {
    const query = `
    subscription OnConversationMetadata($cid: ID!) {
      onConversationMetadata(conversationId: $cid) {
        conversationId
        agentStartResponding
        agentStopResponding
        agentPartialMessage
        agentId
      }
    }
  `;
    return aws_amplify_1.API.graphql({
        query,
        variables: { cid: conversationId },
        authMode: AUTH_MODE,
    }); // Observable
}
// Minimal event validator for safety
function isValidEvent(e) {
    try {
        return !!e &&
            typeof e.id === "string" &&
            typeof e.timestamp === "string" &&
            typeof e.conversationId === "string" &&
            typeof e.sender === "string" &&
            typeof e.agentId === "string" &&
            !!(e.event?.message ||
                e.event?.innerDialog ||
                e.event?.actionRequested ||
                e.event?.actionResult);
    }
    catch {
        return false;
    }
}
function useAgentApiSubscribeConversation(id = "") {
    const setEventsState = (0, recoil_1.useSetRecoilState)(AgentApiStore.ConversationEvents);
    const setPartialResultsMap = (0, recoil_1.useSetRecoilState)(AgentApiStore.ConversationPartialResults);
    // Track the id this hook instance is currently attached to
    const attachedIdRef = (0, react_1.useRef)("");
    (0, react_1.useEffect)(() => {
        if (!id)
            return;
        // Detach from previous id if switching
        if (attachedIdRef.current && attachedIdRef.current !== id) {
            detachFromConversation(attachedIdRef.current);
            attachedIdRef.current = "";
        }
        // Attach (increments ref count; creates subs only if first attach)
        attachToConversation(id, setEventsState, setPartialResultsMap);
        // Remember which id we’re attached to now
        attachedIdRef.current = id;
        // On unmount or id change, detach
        return () => {
            if (attachedIdRef.current) {
                detachFromConversation(attachedIdRef.current);
                attachedIdRef.current = "";
            }
        };
    }, [id, setEventsState, setPartialResultsMap]);
}
/* --------------------------
   Registry attach/detach
---------------------------*/
function attachToConversation(id, setEventsState, setPartialResultsMap) {
    let entry = REGISTRY.get(id);
    if (entry) {
        entry.refs += 1;
        if (DEBUG_SUBS)
            console.debug("[subs] attach: reuse existing for", id, "refs:", entry.refs);
        return;
    }
    // First time: create entry and subscriptions
    entry = {
        refs: 1,
        msgSub: null,
        metaSub: null,
        seenIds: new Set(),
        hadPartial: false,
    };
    REGISTRY.set(id, entry);
    if (DEBUG_SUBS)
        console.debug("[subs] attach: NEW for", id);
    // --- MESSAGES subscription (public WS) ---
    let msgObservable;
    try {
        msgObservable = subscribeConversationMessages(id);
    }
    catch (e) {
        console.error("❌ Failed to create messages subscription observable:", e);
    }
    const msgSub = msgObservable?.subscribe?.({
        next: (sub) => {
            const event = sub?.value?.data?.onConversationEvents;
            if (!isValidEvent(event))
                return;
            if (!isAgentAllowed(event.agentId)) {
                if (DEBUG_SUBS) {
                    console.debug("[subs] dropped event due to agent filter", {
                        conversationId: id,
                        agentId: event.agentId,
                        allowed: ALLOWED_AGENT_IDS ?? "* (all)",
                    });
                }
                return;
            }
            const e = REGISTRY.get(id);
            if (!e)
                return; // detached in the meantime
            // Deduplicate by id
            if (event.id && e.seenIds.has(event.id))
                return;
            if (event.id)
                e.seenIds.add(event.id);
            const hadPartial = !!e.hadPartial;
            // Clear partial for THIS agent when a final message arrives
            if (event.agentId) {
                setPartialResultsMap((prev) => {
                    const convMeta = prev[id] ?? { activeAgentId: null, byAgent: {} };
                    const agentMeta = convMeta.byAgent[event.agentId] ?? {};
                    const nextConv = {
                        // Only set activeAgentId if not already set; don't override a different actively responding agent
                        activeAgentId: convMeta.activeAgentId ?? event.agentId,
                        byAgent: {
                            ...convMeta.byAgent,
                            [event.agentId]: {
                                ...agentMeta,
                                agentPartialMessage: "",
                                updatedAt: nowIso(),
                            },
                        },
                    };
                    // no-op check: if identical, skip write
                    if ((agentMeta.agentPartialMessage || "") === "" &&
                        convMeta.activeAgentId === nextConv.activeAgentId) {
                        return prev;
                    }
                    return { ...prev, [id]: nextConv };
                });
            }
            e.hadPartial = false;
            // Append event to chat log (stable merge, maintain order; subscription events are sequential)
            setEventsState((prev) => {
                const existingEvents = prev[id]?.value ?? [];
                // Avoid duplicates if same event already present
                if (existingEvents.some((x) => x.id === event.id)) {
                    return prev;
                }
                return {
                    ...prev,
                    [id]: state_1.Loadable.loaded([
                        ...existingEvents,
                        { ...event, disableTyping: hadPartial },
                    ]),
                };
            });
        },
        error: (err) => {
            console.error("❌ onConversationEvents subscription error for", id, ":", err);
        },
    });
    // --- METADATA subscription (public WS) ---
    let metaObservable;
    try {
        metaObservable = subscribeConversationMetadata(id);
    }
    catch (e) {
        console.error("❌ Failed to create metadata subscription observable:", e);
    }
    const metaSub = metaObservable?.subscribe?.({
        next: (sub) => {
            const metadata = sub?.value?.data?.onConversationMetadata;
            if (!metadata)
                return;
            const e = REGISTRY.get(id);
            if (!e)
                return; // detached
            const agentId = metadata.agentId || "";
            if (!agentId) {
                // If agentId is missing, we cannot agent-scope; ignore to prevent cross-agent bleed.
                return;
            }
            // Always allow typing/partial metadata, even if agent isn't whitelisted.
            const looksLikeTyping = metadata?.agentStartResponding === true ||
                (typeof metadata?.agentPartialMessage === "string" && metadata.agentPartialMessage.trim().length > 0);
            if (!isAgentAllowed(agentId) && !looksLikeTyping)
                return;
            setPartialResultsMap((prev) => {
                const convMeta = prev[id] ?? { activeAgentId: null, byAgent: {} };
                const current = convMeta.byAgent[agentId] ?? {};
                // Prefer latest partial exactly as sent (server often sends cumulative content).
                // Ignore EMPTY strings coming from the wire to avoid erasing the visible hint mid-stream.
                const incomingPartial = typeof metadata.agentPartialMessage === "string"
                    ? metadata.agentPartialMessage
                    : undefined;
                const nextPartial = incomingPartial !== undefined
                    ? (incomingPartial.trim().length > 0
                        ? incomingPartial
                        : (current.agentPartialMessage ?? "")) // keep last non-empty
                    : (current.agentPartialMessage ?? "");
                // Track whether a partial is “active” for this conversation (used to style the next final bubble)
                e.hadPartial = !!(nextPartial && nextPartial.trim().length > 0);
                const nextAgentMeta = {
                    ...current,
                    ...(metadata.agentStartResponding !== null &&
                        metadata.agentStartResponding !== undefined && {
                        agentStartResponding: !!metadata.agentStartResponding,
                        ...(metadata.agentStartResponding ? { agentStopResponding: false } : {}),
                    }),
                    ...(metadata.agentStopResponding !== null &&
                        metadata.agentStopResponding !== undefined && {
                        agentStopResponding: !!metadata.agentStopResponding,
                    }),
                    agentPartialMessage: nextPartial,
                    updatedAt: nowIso(),
                };
                // Only follow this agent as "active" if it is actually starting or emitting a (non-empty) partial.
                const followAsActive = metadata.agentStartResponding === true ||
                    (nextPartial && nextPartial.trim().length > 0);
                const nextConvMeta = {
                    activeAgentId: followAsActive ? agentId : (convMeta.activeAgentId ?? null),
                    byAgent: {
                        ...convMeta.byAgent,
                        [agentId]: nextAgentMeta,
                    },
                };
                // no-op check
                const unchanged = convMeta.activeAgentId === nextConvMeta.activeAgentId &&
                    (current.agentStartResponding ?? null) === (nextAgentMeta.agentStartResponding ?? null) &&
                    (current.agentStopResponding ?? null) === (nextAgentMeta.agentStopResponding ?? null) &&
                    (current.agentPartialMessage || "") === (nextAgentMeta.agentPartialMessage || "");
                if (unchanged)
                    return prev;
                return { ...prev, [id]: nextConvMeta };
            });
        },
        error: (err) => {
            console.error("❌ onConversationMetadata subscription error for", id, ":", err);
        },
    });
    entry.msgSub = msgSub || null;
    entry.metaSub = metaSub || null;
    // One-time backfill to cover the race while subs establish.
    // OPTION A ALIGNMENT: use PRIVATE proxy for backfill so we see the agent welcome immediately.
    window.setTimeout(() => {
        void backfillOncePrivate(id, setEventsState);
    }, 450 + Math.floor(Math.random() * 150));
}
function detachFromConversation(id) {
    const entry = REGISTRY.get(id);
    if (!entry)
        return;
    entry.refs -= 1;
    if (DEBUG_SUBS)
        console.debug("[subs] detach for", id, "remaining refs:", entry.refs);
    if (entry.refs > 0)
        return;
    // Last consumer gone — tear down sockets and drop registry
    try {
        entry.msgSub?.unsubscribe?.();
    }
    catch (e) {
        console.warn("⚠️ Unsubscribe error (messages):", e);
    }
    try {
        entry.metaSub?.unsubscribe?.();
    }
    catch (e) {
        console.warn("⚠️ Unsubscribe error (metadata):", e);
    }
    REGISTRY.delete(id);
}
/* --------------------------
   One-time backfill (PRIVATE)
---------------------------*/
function buildPrivateEventsQuery(salt) {
    return new shared_1.GraphqlQuery(`
    # backfill-salt: ${salt}
    query Events($cid: ID!) {
      eventsByConversationId(conversationId: $cid) {
        id
        sender
        timestamp
        conversationId
        userId
        agentId
        event { message innerDialog actionRequested actionResult }
      }
    }`, true // ✅ use ID token (private /graphql proxy)
    );
}
async function backfillOncePrivate(id, setEventsState) {
    // If we already have any events for this id, skip
    let hasEventsAlready = false;
    setEventsState((prev) => {
        hasEventsAlready = (prev[id]?.value?.length ?? 0) > 0;
        return prev;
    });
    if (hasEventsAlready) {
        if (DEBUG_SUBS)
            console.debug("[backfill] skip; already have events for", id);
        return;
    }
    try {
        if (DEBUG_SUBS)
            console.debug("[backfill] endpoint=private (/graphql via ID token)");
        const q = buildPrivateEventsQuery(String(Date.now()));
        const anyQ = q;
        const run = anyQ.invoke ?? anyQ.execute ?? anyQ.run ?? anyQ.fetch;
        if (typeof run !== "function") {
            throw new Error("GraphqlQuery has no supported execute method (expected invoke/execute/run/fetch).");
        }
        // We used $cid in the query — pass { cid: id }
        const data = await run.call(q, { cid: id });
        // Normalize both shapes:
        // { eventsByConversationId } OR { data: { eventsByConversationId } }
        const rowsRaw = data?.eventsByConversationId ??
            data?.data?.eventsByConversationId ??
            [];
        const rows = (Array.isArray(rowsRaw) ? rowsRaw : []);
        // Merge uniquely by id and sort by timestamp asc
        setEventsState((prev) => {
            const existing = prev[id]?.value ?? [];
            const byId = new Map();
            for (const e of existing)
                if (e?.id)
                    byId.set(e.id, e);
            for (const e of rows)
                if (isValidEvent(e) && e.id && !byId.has(e.id))
                    byId.set(e.id, e);
            const merged = Array.from(byId.values()).sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
            return { ...prev, [id]: state_1.Loadable.loaded(merged) };
        });
        // Seed seenIds so subsequent subscription duplicates are dropped
        const entry = REGISTRY.get(id);
        if (entry) {
            for (const e of rows)
                if (isValidEvent(e) && e.id)
                    entry.seenIds.add(e.id);
        }
    }
    catch (e) {
        // Quietly log; no public fallback under Option A
        console.warn("backfillOncePrivate failed:", e);
    }
}
